"use client";

import React from "react";
import { Controller, Control, Path, RegisterOptions } from "react-hook-form";
import Select from "react-select";
import clsx from "clsx";

interface Option {
  label: string;
  value: any;
}

interface FormSelectProps<T extends Record<string, any>> {
  name: Path<T>;
  control: Control<T>;
  label?: string;
  options: any[];
  displayOption?: (option: any) => Option;
  transformer?: (value: any) => any;
  placeholder?: string;
  isDisabled?: boolean;
  required?: boolean;
  rules?: Omit<RegisterOptions<T, Path<T>>, "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"> | undefined;
}

export default function FormSelect<T extends Record<string, any>>({
  name,
  control,
  label,
  options,
  displayOption,
  transformer,
  required,
  placeholder,
  rules,
  ...props
}: FormSelectProps<T>) {
  // Default display option function if not provided
  const defaultDisplayOption = (option: any): Option =>
    option.label
      ? option
      : {
          label: options.find((o: any) => o.value === option)?.label || option,
          value: option.value || option,
        };

  // Use provided displayOption function or fall back to default
  const getOptionDisplay = displayOption || defaultDisplayOption;

  // Default transformer function if not provided
  const defaultTransformer = (value: any): any => value;

  // Use provided transformer function or fall back to default
  const transformValue = transformer || defaultTransformer;

  const selectClassNames = {
    control: (state: { isFocused: boolean }) =>
      clsx(
        "flex items-center bg-secondary-light border border-secondary-light rounded-2xl",
        "min-h-[42px] hover:cursor-pointer hover:border-grey-border p-3",
        "transition-all duration-300",
        state.isFocused ? "border-grey-border ring-1 ring-grey-border" : "outline-gray-300",
      ),
    valueContainer: () => "flex flex-wrap flex-1 px-3 py-1 gap-1",
    placeholder: () => "inter text-secondary-foreground text-base",
    input: () => "text-secondary-foreground font-medium text-sm inter focus:outline-none p-0 m-0",
    menu: () => "mt-1 mb-2 border border-secondary-light rounded-2xl bg-white shadow-lg absolute w-full z-50",
    menuList: () => "max-h-60 inter font-medium overflow-auto no-scrollbar",
    option: ({ isSelected, isFocused }: { isSelected: boolean; isFocused: boolean }) =>
      clsx(
        "px-4 py-2 cursor-pointer inter font-medium first:rounded-t-2xl last:rounded-b-2xl",
        isSelected && "bg-secondary-light text-black rounded-xl",
        !isSelected && isFocused && "bg-secondary-light text-black overflow-hidden",
        !isSelected && !isFocused && "text-gray-800 hover:bg-gray-50",
      ),
    singleValue: () => "text-[#000000] inter font-medium text-sm",
    indicatorsContainer: () => "flex items-center px-2",
    dropdownIndicator: (state: { selectProps: { menuIsOpen: boolean } }) =>
      clsx(
        "text-gray-400 hover:text-gray-600 transition-transform duration-300",
        state.selectProps.menuIsOpen && "rotate-180",
      ),
    clearIndicator: () => "text-primary p-1",
    indicatorSeparator: () => "hidden",
    noOptionsMessage: () => "text-secondary-foreground p-4",
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState: { error } }) => (
        <div className="w-full">
          {label && (
            <label className="inter mb-1 block text-sm font-semibold text-[#000000]">
              {label}
              {required && <span className="ml-1 text-red-500">*</span>}
            </label>
          )}
          <Select
            instanceId={name}
            unstyled
            options={options.map(getOptionDisplay)}
            value={field.value ? getOptionDisplay(field.value) : null}
            onChange={(selectedOption) => {
              if (selectedOption) {
                const transformedValue = transformValue(selectedOption.value);
                field.onChange(transformedValue);
              } else {
                field.onChange(null);
              }
            }}
            placeholder={required ? `${placeholder} *` : placeholder}
            onBlur={field.onBlur}
            classNames={selectClassNames}
            {...props}
          />
          {error && <div className="mt-1 text-sm text-red-500">{error.message}</div>}
        </div>
      )}
    />
  );
}
